import React, { useMemo } from "react";
import { useUser, useOrganizationList, useOrganization as useClerkOrganization } from "@clerk/clerk-react";
import { Avatar } from "antd";
import { TeamOutlined } from "@ant-design/icons";

/**
 * 生成组织选项的 icon
 */
const createOrganizationIcon = (imageUrl?: string | null) => {
  if (imageUrl) {
    return React.createElement(Avatar, {
      size: 32,
      shape: "square",
      src: imageUrl,
      className: "rounded",
    });
  }
  return React.createElement(TeamOutlined, {
    className: "text-gray-500 text-xl",
  });
};

/**
 * 获取当前用户的所有组织列表
 * @returns 组织列表数据及相关方法
 */
export const useAllOrganizations = () => {
  const { user } = useUser();
  const { organization } = useClerkOrganization();
  
  const { userMemberships, userInvitations, userSuggestions } = useOrganizationList({
    userMemberships: {
      infinite: true,
      pageSize: 500,
    },
    userInvitations: {
      infinite: true,
      pageSize: 500,
    },
    userSuggestions: {
      infinite: true,
      pageSize: 500,
    },
  });

  // 获取所有组织成员关系（原始数据）
  const allOrganizationMemberships = useMemo(() => {
    return user?.organizationMemberships || [];
  }, [user?.organizationMemberships]);

  // 获取所有组织列表（仅组织信息）
  const organizations = useMemo(() => {
    return allOrganizationMemberships.map((membership) => membership.organization);
  }, [allOrganizationMemberships]);

  // 获取所有组织 ID 列表
  const organizationIds = useMemo(() => {
    return allOrganizationMemberships.map(
      (membership) => membership.organization.id
    );
  }, [allOrganizationMemberships]);

  // 获取格式化的组织选项列表（用于 Select 等组件）
  const orgListOptions = useMemo(() => {
    return allOrganizationMemberships
      ?.map((membership) => ({
        value: membership.organization.id,
        label: membership.organization.name,
        icon: createOrganizationIcon(membership.organization.imageUrl),
        // 额外保存组织信息，方便使用
        organization: membership.organization,
      }))
      ?.reverse() || [];
  }, [allOrganizationMemberships]);

  // 刷新组织列表
  const refresh = () => {
    userMemberships?.revalidate?.();
    userInvitations?.revalidate?.();
    userSuggestions?.revalidate?.();
  };

  return {
    // 原始数据
    allOrganizationMemberships, // 所有组织成员关系
    organizations, // 所有组织列表
    organizationIds, // 所有组织 ID 列表
    orgListOptions, // 格式化的组织选项列表（用于 Select 等组件）
    userMemberships, // Clerk 的 userMemberships 对象（包含 revalidate 等方法）
    userInvitations, // 邀请列表
    userSuggestions, // 建议列表
    
    // 当前组织
    currentOrganization: organization,
    
    // 方法
    refresh, // 刷新组织列表
    
    // 状态
    isLoading: userMemberships?.isLoading || false,
    invitationCount: userInvitations?.data?.length || 0,
  };
};

